/* eslint-disable radix */
import { useCallback, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
// eslint-disable-next-line import/no-extraneous-dependencies
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/dialog';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  useGetUserQuery,
  useLazyGetVerificationCodeQuery,
  useValidateVerificationCodeMutation,
} from './auth.api.slice';

const FormSchema = z.object({
  code: z.string().min(6, {
    message: 'Your verification code must be 6 characters.',
  }),
});

const INITIAL_RESEND_DELAY = 30;
const MAX_RESEND_DELAY = 300;
const RAPID_RESEND_THRESHOLD = 30;
const RESET_TIME_THRESHOLD = 900;

function VerifyAccountModal() {
  const [resendTimer, setResendTimer] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const [lastResendTime, setLastResendTime] = useState(0);
  const [open, setOpen] = useState(false);

  const [getVerificationCode, { isLoading: isGetVerificationCodeLoading }] =
    useLazyGetVerificationCodeQuery();
  const [validateVerificationCode, { isLoading: isValidateLoading }] =
    useValidateVerificationCodeMutation();
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      code: '',
    },
  });
  const { refetch: refetchUser } = useGetUserQuery();

  const calculateResendDelay = useCallback(
    (count: number) => {
      const now = Date.now();
      const timeSinceLastResend = (now - lastResendTime) / 1000;

      let delay = INITIAL_RESEND_DELAY * 2 ** count;

      if (timeSinceLastResend < RAPID_RESEND_THRESHOLD) {
        delay *= 2;
      }

      return Math.min(delay, MAX_RESEND_DELAY);
    },
    [lastResendTime]
  );

  useEffect(() => {
    const lastSentTime = localStorage.getItem('lastOtpSentTime');
    const storedResendCount = localStorage.getItem('otpResendCount');

    if (lastSentTime) {
      const timeSinceLastResend = Math.floor(
        (Date.now() - parseInt(lastSentTime)) / 1000
      );

      if (timeSinceLastResend > RESET_TIME_THRESHOLD) {
        setResendCount(0);
        setResendTimer(0);
        setLastResendTime(0);
        localStorage.removeItem('lastOtpSentTime');
        localStorage.removeItem('otpResendCount');
      } else {
        const delay = calculateResendDelay(
          storedResendCount ? parseInt(storedResendCount) : 0
        );
        setResendTimer(Math.max(delay - timeSinceLastResend, 0));
      }
    }

    if (storedResendCount) {
      setResendCount(parseInt(storedResendCount));
    }
  }, [calculateResendDelay]);

  useEffect(() => {
    let timer: any;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleResend = async () => {
    await getVerificationCode().unwrap();
    toast.success('Verifiction code re-sent successfully');
    const now = Date.now();
    const timeSinceLastResend = (now - lastResendTime) / 1000;
    const newResendCount = resendCount + 1;

    if (timeSinceLastResend < RAPID_RESEND_THRESHOLD) {
      return;
    }

    setResendCount(newResendCount);
    setLastResendTime(now);

    const delay = calculateResendDelay(newResendCount);
    setResendTimer(delay);

    localStorage.setItem('lastOtpSentTime', now.toString());
    localStorage.setItem('otpResendCount', newResendCount.toString());
  };

  async function handleValidateOtpSubmit(values: z.infer<typeof FormSchema>) {
    try {
      const data = await validateVerificationCode(values.code).unwrap();
      if (data.status !== 'success') {
        toast.error('OTP validation failed', {
          description: (data as any)?.errorMessage || 'Something went wrong',
        });
        return;
      }
      setResendCount(0);
      setResendTimer(0);
      localStorage.removeItem('lastOtpSentTime');
      localStorage.removeItem('otpResendCount');
      toast.success('OTP validated successfully');
      await refetchUser();
      setOpen(!open);
    } catch (error: any) {
      toast.error('OTP validation failed', {
        description: error?.data?.errorMessage || 'Something went wrong',
      });
    }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="link"
          size="sm"
          className="px-0 font-semibold text-blue-500 hover:text-blue-600 underline"
        >
          Enter OTP here
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[20px] sm:text-2xl font-primary text-left">
            Filefinity Signup
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4 items-center justify-center"
            onSubmit={form.handleSubmit(handleValidateOtpSubmit)}
          >
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <div className="flex justify-center flex-col items-center gap-4">
                    <FormLabel>Enter your OTP Code</FormLabel>
                    <FormControl>
                      <InputOTP
                        maxLength={6}
                        {...field}
                        pattern={REGEXP_ONLY_DIGITS}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                  </div>
                  <div className="text-center w-full">
                    {resendTimer > 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Resend available in {resendTimer} seconds
                      </p>
                    ) : (
                      <Button
                        type="button"
                        variant="link"
                        className="text-sm p-0 h-auto"
                        disabled={isGetVerificationCodeLoading}
                        onClick={() => handleResend()}
                      >
                        Click here to resend
                      </Button>
                    )}
                  </div>
                  <FormDescription className="text-center">
                    Check your email for the OTP. If not received in 2-3
                    minutes, check spam.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="w-full gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Close
                </Button>
              </DialogClose>
              <Button type="submit" disabled={isValidateLoading}>
                Submit
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default VerifyAccountModal;
